import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "../lib/ui";
import { Plus, Calendar, Trash2 } from "lucide-react";

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [show, setShow] = useState(false);
  const { user } = useAuth();
  const canCreate = ["master_admin", "admin", "manager"].includes(user?.role);
  const load = () => api.get("/meetings").then((r) => setMeetings(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6">
      <PageHeader title="Meetings" subtitle="Meeting action tracker — action items auto-create tasks">
        {canCreate && <Button onClick={() => setShow(true)} data-testid="new-meeting-button"><Plus size={16} className="mr-1" /> New Meeting</Button>}
      </PageHeader>
      <div className="space-y-3">
        {meetings.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No meetings logged.</div>}
        {meetings.map((m) => (
          <div key={m.id} className="bg-white border rounded-xl p-5">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-blue-600" />
              <span className="font-semibold text-slate-800">{m.name}</span>
              <span className="text-xs text-slate-400">{fmtDate(m.date)}</span>
              <span className="ml-auto text-xs bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">{m.completed} done</span>
              <span className="text-xs bg-amber-50 text-amber-700 rounded-full px-2 py-0.5">{m.pending} pending</span>
            </div>
            {m.decision && <div className="text-sm text-slate-600 mt-2"><span className="font-medium">Decision:</span> {m.decision}</div>}
            {m.actions?.length > 0 && (
              <div className="mt-3 space-y-1">
                <div className="text-xs font-semibold text-slate-500 uppercase">Action Items</div>
                {m.actions.map((a) => (
                  <div key={a.id} className="text-sm flex items-center gap-2 border-l-2 border-blue-200 pl-2">
                    <span className={`h-2 w-2 rounded-full ${a.status === "Completed" ? "bg-emerald-500" : "bg-amber-500"}`} />
                    {a.title} <span className="text-xs text-slate-400 ml-auto">{fmtDate(a.due_date)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {show && <MeetingModal onClose={() => setShow(false)} onDone={() => { setShow(false); load(); }} />}
    </div>
  );
}

function MeetingModal({ onClose, onDone }) {
  const { users, departments } = useData();
  const [f, setF] = useState({ name: "", date: "", discussion: "", decision: "" });
  const [actions, setActions] = useState([{ title: "", owner_id: "", department_id: "", due_date: "", priority: "Medium" }]);
  const setAct = (i, k, v) => setActions((p) => p.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const submit = async () => {
    if (!f.name) return toast.error("Meeting name required");
    const payload = { ...f, date: f.date || null, action_items: actions.filter((a) => a.title).map((a) => ({
      title: a.title, owner_id: a.owner_id || null, department_id: a.department_id || null, due_date: a.due_date || null, priority: a.priority })) };
    await api.post("/meetings", payload);
    toast.success("Meeting logged, tasks created for action items"); onDone();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="meeting-modal">
        <DialogHeader><DialogTitle>New Meeting</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Meeting Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          </div>
          <div><Label>Discussion</Label><Textarea value={f.discussion} onChange={(e) => setF({ ...f, discussion: e.target.value })} rows={2} /></div>
          <div><Label>Decision</Label><Textarea value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })} rows={2} /></div>
          <div>
            <Label>Action Items (auto-create tasks)</Label>
            {actions.map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-1.5 mt-1.5 items-center">
                <Input className="col-span-5" placeholder="Action" value={a.title} onChange={(e) => setAct(i, "title", e.target.value)} />
                <div className="col-span-3">
                  <Select value={a.owner_id} onValueChange={(v) => setAct(i, "owner_id", v)}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Owner" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-3" type="date" value={a.due_date} onChange={(e) => setAct(i, "due_date", e.target.value)} />
                <button className="col-span-1 text-slate-400" onClick={() => setActions(actions.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setActions([...actions, { title: "", owner_id: "", department_id: "", due_date: "", priority: "Medium" }])}>+ Add action</Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="meeting-submit">Create Meeting</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
